import {
  campaignCityIds,
  normalizeStepPromo,
  stepHasPromoVariable,
} from "./promo";
import { emailVariableTexts } from "./emailBlocks";
import {
  copyStepsForNewCampaign,
  unreadPushFallbackErrors,
} from "./campaignSteps";
import { emptyPush, isPushOnly, pushVariableTexts, validatePush } from "./push";
import { emptyCondition, isMessage, scenarioErrors } from "./scenario";
import {
  guestFeatureLabel,
  guestFeatureRuleErrors,
  isGuestFeatureField,
} from "./guestFeatures";
import type {
  CampaignTemplate,
  Capability,
  ConditionGroup,
  EmailContent,
  MarketingBootstrap,
  MarketingCondition,
  MarketingDraft,
  MarketingNodeType,
  MarketingStep,
} from "./types";

export interface ConditionField {
  label: string;
  type: "number" | "boolean";
  unit?: string;
  capability?: string;
}

export const CONDITION_FIELDS: Record<string, ConditionField> = {
  daysSinceLastOrder: { label: "Дней без заказа", type: "number", unit: "дн." },
  ordersCount: { label: "Всего заказов", type: "number", unit: "заказов" },
  ordersLast30Days: {
    label: "Заказов за последние 30 дней",
    type: "number",
    unit: "заказов",
  },
  avgCheck: { label: "Средний чек", type: "number", unit: "₽" },
  revenue: { label: "Сумма всех заказов", type: "number", unit: "₽" },
  daysSinceFirstOrder: {
    label: "Дней после первого заказа",
    type: "number",
    unit: "дн.",
  },
  sourceStarter: { label: "Есть заказ из Starter", type: "boolean" },
  pushReady: { label: "Есть приложение (дойдёт пуш)", type: "boolean" },
  emailReady: { label: "Email доступен", type: "boolean" },
  smsReady: { label: "SMS доступна", type: "boolean" },
  ordersInPeriod: {
    label: "Заказы за период",
    type: "number",
    unit: "заказов",
  },
  birthdayInDays: {
    label: "Дней до дня рождения",
    type: "number",
    unit: "дн.",
    capability: "birthday",
  },
};

/** Поля развилки сценария: смотрят на сообщения текущего прохождения, в аудитории кампании недоступны. */
export const RUN_FIELDS: Record<string, ConditionField> = {
  pushOpened: { label: "Push прочитан", type: "boolean" },
  emailOpened: { label: "Письмо открыто", type: "boolean" },
  emailClicked: { label: "Нажал ссылку в письме", type: "boolean" },
};

export const CHANNEL_LABELS = {
  sms: "SMS",
  email: "Электронная почта",
  push: "Пуш приложения",
};

export function isAvailable(capability: Capability | undefined): boolean {
  return typeof capability === "boolean"
    ? capability
    : capability?.available === true;
}

export function capabilityReason(capability: Capability | undefined): string {
  return typeof capability === "object" && capability.reason
    ? capability.reason
    : "Источник или канал пока не подключён";
}

export function emptyEmail(): EmailContent {
  return {
    templateId: "",
    subject: "",
    preheader: "",
    title: "",
    body: "",
    imageUrl: "",
    buttonText: "",
    buttonUrl: "",
    footer: "",
  };
}

export function newStep(
  channel: MarketingStep["channel"] = "sms",
): MarketingStep {
  return {
    id: crypto.randomUUID(),
    channel,
    waitHours: 24,
    fallback: "no_order",
    smsText: "",
    email: emptyEmail(),
    ...(channel === "push" ? { push: emptyPush() } : {}),
    promoMode: "none",
    promoCode: "",
    promoPoolId: "",
  };
}

/**
 * Новый узел сценария. Служебные узлы живут в том же массиве шагов, что и
 * сообщения: тип пустой — значит сообщение, как было до появления сценариев.
 */
export function newNode(type: MarketingNodeType): MarketingStep {
  const base = newStep("sms");
  if (type === "message") return base;
  const node: MarketingStep = { ...base, type, waitHours: 0 };
  if (type === "delay") node.waitHours = 24;
  if (type === "segmentSplit") node.split = { condition: emptyCondition() };
  if (type === "randomCohort" || type === "randomSplit")
    node.cohorts = [{ percent: 50 }, { percent: 50 }];
  if (type === "contactLimit") node.contactLimit = { max: 2 };
  return node;
}

export function newDraft(bootstrap?: MarketingBootstrap): MarketingDraft {
  return {
    name: "Вернуть гостей после паузы",
    autoNameBase: "Вернуть гостей после паузы",
    tenantId: "",
    tenantIds: [],
    partnerId: bootstrap?.scope.partnerId ?? "",
    companyId:
      bootstrap?.companies.length === 1 ? bootstrap.companies[0].id : "",
    mode: "once",
    scheduledAt: null,
    promoCode: "",
    condition: {
      kind: "group",
      op: "all",
      children: [
        { kind: "rule", field: "daysSinceLastOrder", op: "gte", value: 30 },
        { kind: "rule", field: "ordersCount", op: "gte", value: 2 },
      ],
    },
    steps: [newStep()],
    frequencyDays: 7,
    reentryDays: 30,
    attributionWindowDays: 3,
    budget: "0",
    unlimitedBudget: true,
    holdoutPercent: 10,
    sendHour: 11,
    sendTimeMode: "fixed",
    sendOffsetMinutes: 60,
    stopOnOrder: true,
  };
}

export function draftFromTemplate(
  template: CampaignTemplate,
  bootstrap: MarketingBootstrap,
): MarketingDraft {
  const draft = newDraft(bootstrap);
  return {
    ...draft,
    name: template.name,
    goal: template.goal,
    autoNameBase: template.name,
    condition: structuredClone(template.audience),
    mode: template.mode,
    frequencyDays: template.frequencyDays,
    reentryDays: template.reentryDays,
    sendHour: template.sendHour,
    steps: template.defaultSteps?.length
      ? copyStepsForNewCampaign(template.defaultSteps)
      : [
          {
            ...newStep(),
            smsText: template.defaultSmsText,
            email: emptyEmail(),
          },
        ],
  };
}

export function newPushDraft(bootstrap?: MarketingBootstrap): MarketingDraft {
  return {
    ...newDraft(bootstrap),
    name: "Разовый пуш",
    autoNameBase: "Разовый пуш",
    mode: "once",
    condition: {
      kind: "group",
      op: "all",
      children: [{ kind: "rule", field: "ordersCount", op: "gte", value: 1 }],
    },
    steps: [newStep("push")],
  };
}

/** Строковая сумма остаётся точной: денежная арифметика выполняется сервером.
 * Дробную часть показываем ровно двумя знаками: числа из JSON приходят с хвостом
 * плавающей точки (3.9500000000000004), показывать его нельзя. */
export function formatMoney(value: string | number | null | undefined): string {
  if (value == null || !/^-?\d+(?:\.\d+)?$/.test(String(value))) return "—";
  const [whole, fraction = ""] = String(value).split(".");
  const cents = fraction ? fraction.padEnd(2, "0").slice(0, 2) : "";
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0")}${cents ? `,${cents}` : ""} ₽`;
}

export function normalizeMoney(value: string): string {
  return value.trim().replace(",", ".");
}

export function conditionLabel(node: MarketingCondition): string {
  if (node.kind === "group")
    return `(${node.children.map(conditionLabel).join(node.op === "all" ? " И " : " ИЛИ ")})`;
  if (isGuestFeatureField(node.field)) return guestFeatureLabel(node);
  const field = CONDITION_FIELDS[node.field];
  if (!field) return "Неизвестное условие";
  if (field.type === "boolean")
    return `${field.label}: ${String(node.value) === "true" ? "да" : "нет"}`;
  return `${field.label} ${node.op === "gte" ? "не меньше" : node.op === "lte" ? "не больше" : "равно"} ${node.value} ${field.unit ?? ""}`.trim();
}

export function replaceCondition(
  root: ConditionGroup,
  path: number[],
  replacement: MarketingCondition | null,
): ConditionGroup {
  if (!path.length) return replacement?.kind === "group" ? replacement : root;
  const [index, ...rest] = path;
  return {
    ...root,
    children: root.children.flatMap((child, position) => {
      if (position !== index) return [child];
      if (rest.length)
        return child.kind === "group"
          ? [replaceCondition(child, rest, replacement)]
          : [child];
      return replacement ? [replacement] : [];
    }),
  };
}

export function safeImageUrl(value: string): string | undefined {
  if (!value) return undefined;
  if (value.startsWith("/") && !value.startsWith("//")) return value;
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? value : undefined;
  } catch {
    return undefined;
  }
}

/** Понятные ошибки выбора письма; полный контракт повторно проверяет сервер. */
export function validateCampaignEmail(email: EmailContent): string[] {
  const errors: string[] = [];
  if (email.formatVersion === 1) {
    if (!email.subject.trim() || /[\r\n]/.test(email.subject))
      errors.push("заполните тему письма одной строкой в разделе «Шаблоны».");
    if (email.templateId !== "blocks-v1")
      errors.push("обновите формат письма в разделе «Шаблоны».");
    if (!email.blocks?.length || email.blocks.length > 30)
      errors.push("письмо должно содержать от 1 до 30 блоков.");
    const links = [
      email.logoUrl,
      ...(email.footerLinks?.map((item) => item.url) ?? []),
    ];
    const ids = new Set<string>();
    for (const block of email.blocks ?? []) {
      if (!block.id || ids.has(block.id))
        errors.push("у блоков письма должны быть разные идентификаторы.");
      ids.add(block.id);
      if (
        ![
          "hero",
          "text",
          "image",
          "button",
          "promo",
          "products",
          "spacer",
          "divider",
        ].includes(block.type)
      )
        errors.push("письмо содержит неизвестный блок. Обновите шаблон.");
      links.push(
        block.url,
        block.imageUrl,
        ...(block.spans?.map((span) => span.href) ?? []),
      );
      for (const item of block.items ?? []) links.push(item.url, item.imageUrl);
      if (block.type === "image" && !block.imageUrl)
        errors.push("загрузите фотографию в блок письма.");
      if (
        (block.type === "button" && !block.url) ||
        (block.url && block.type !== "image" && !block.buttonText?.trim())
      )
        errors.push("заполните текст и HTTPS-ссылку кнопки в шаблоне.");
      if (
        block.type === "products" &&
        (!block.items?.length ||
          block.items.length > 3 ||
          block.items.some((item) => !item.title.trim()))
      )
        errors.push("в товарном блоке нужны от 1 до 3 блюд с названиями.");
    }
    if (email.footerLinks?.some((item) => !item.label.trim() || !item.url))
      errors.push("заполните название и адрес ссылок внизу письма.");
    for (const link of links.filter((item): item is string => !!item)) {
      try {
        const url = new URL(link);
        if (
          url.protocol !== "https:" ||
          url.username ||
          url.password ||
          /[{}]/.test(url.host) ||
          /[\\\r\n\t]/.test(link) ||
          link.trim() !== link
        )
          throw new Error();
      } catch {
        errors.push(
          "в письме разрешены полные HTTPS-ссылки; переменные нельзя указывать в домене.",
        );
      }
    }
    return [...new Set(errors)];
  }
  if (!email.subject.trim() || !email.title.trim() || !email.body.trim())
    errors.push("заполните тему, заголовок и текст письма.");
  if (!email.templateId) errors.push("выберите основу письма из каталога.");
  if (email.imageUrl && !safeImageUrl(email.imageUrl))
    errors.push(
      "изображение должно быть загружено в систему или доступно по HTTPS.",
    );
  if (email.buttonText && !/^https:\/\//.test(email.buttonUrl))
    errors.push("укажите HTTPS-ссылку кнопки письма.");
  if (email.buttonUrl && !email.buttonText.trim())
    errors.push("заполните надпись на кнопке письма.");
  return errors;
}

/** Предпросмотр текста, не расчёт цены или доказательство доступности получателя. */
export function personalize(
  text: string,
  name = "Гость",
  promo = "",
  dish = "Филадельфия",
): string {
  return text
    .replace(
      /\{favorite_dish\}|\{любимое_блюдо\}/g,
      () => dish || "ваши любимые блюда",
    )
    .replace(/\{name\}|\{имя\}/g, () => name)
    .replace(/\{promo\}|\{промокод\}/g, () => promo)
    .replace(/\{unsubscribe\}|\{отписка\}/g, "[ссылка для отказа]");
}

export function validateDraft(
  draft: MarketingDraft,
  bootstrap: MarketingBootstrap,
  {
    checkChannelAvailability = true,
  }: { checkChannelAvailability?: boolean } = {},
): string[] {
  const errors: string[] = [];
  const paid = !isPushOnly(draft.steps);
  if (paid && draft.payerCompanyId === "hq" && !bootstrap.scope.isAdmin)
    errors.push(
      "Плательщика «Управляющая компания» может выбрать только администратор.",
    );
  if (
    paid &&
    draft.payerCompanyId &&
    draft.payerCompanyId !== "hq" &&
    !bootstrap.companies.some((c) => c.id === draft.payerCompanyId)
  )
    errors.push("Выберите доступное вам юридическое лицо для оплаты.");
  if (!draft.name.trim()) errors.push("Назовите кампанию.");
  const cityIds = campaignCityIds(draft);
  if (!cityIds.length) errors.push("Выберите города кампании: хотя бы один.");
  if (cityIds[0] !== draft.tenantId)
    errors.push("Обновите выбор городов кампании.");
  if (new Set(cityIds).size !== cityIds.length)
    errors.push("Города кампании не должны повторяться.");
  if (!draft.companyId) errors.push("Выберите юридическое лицо кампании.");
  if (draft.partnerId && draft.partnerId !== draft.companyId)
    errors.push("Владелец кампании и юридическое лицо должны совпадать.");
  if (
    paid &&
    !draft.unlimitedBudget &&
    !/^\d+(?:\.\d{1,2})?$/.test(normalizeMoney(draft.budget))
  )
    errors.push(
      "Укажите лимит расходов в рублях, не больше двух знаков после запятой.",
    );
  let rules = 0;
  const walk = (node: MarketingCondition, depth = 0, scenario = false) => {
    if (node.kind === "group") {
      if (!node.children.length)
        errors.push(
          "Пустая группа условий: добавьте условие или удалите группу.",
        );
      if (depth > 2) errors.push("Допустимы три уровня групп условий.");
      node.children.forEach((child) => walk(child, depth + 1, scenario));
      return;
    }
    rules += 1;
    if (isGuestFeatureField(node.field)) {
      errors.push(...guestFeatureRuleErrors(node));
      return;
    }
    const field =
      (scenario && RUN_FIELDS[node.field]) || CONDITION_FIELDS[node.field];
    if (!field) {
      errors.push("Условие использует неизвестное поле.");
      return;
    }
    if (
      node.field === "ordersInPeriod" &&
      (!Number.isInteger(node.periodMinutes) ||
        (node.periodMinutes ?? 0) < 1 ||
        (node.periodMinutes ?? 0) > 525600)
    )
      errors.push("Укажите период заказов от 1 минуты до 365 дней.");
    if (
      field.capability &&
      !isAvailable(bootstrap.capabilities[field.capability])
    )
      errors.push(`${field.label}: нет доступного источника данных.`);
    if (
      field.type === "boolean" &&
      (typeof node.value !== "boolean" || node.op !== "eq")
    )
      errors.push(`${field.label}: выберите да или нет.`);
    if (
      field.type === "number" &&
      (node.value === "" ||
        !Number.isFinite(Number(node.value)) ||
        Number(node.value) < 0)
    )
      errors.push(`${field.label}: введите число не меньше нуля.`);
    if (
      field.type === "number" &&
      field.unit !== "₽" &&
      !Number.isSafeInteger(Number(node.value))
    )
      errors.push(`${field.label}: нужно целое число.`);
  };
  walk(draft.condition);
  if (rules > 30) errors.push("В аудитории допускается не больше 30 условий.");
  if (
    paid &&
    !draft.unlimitedBudget &&
    Number(normalizeMoney(draft.budget)) < 1
  )
    errors.push("Лимит кампании — не меньше 1 рубля.");
  if (
    draft.companyId &&
    !bootstrap.companies.some((item) => item.id === draft.companyId)
  )
    errors.push("Компания недоступна вашему аккаунту.");
  if (
    draft.companyId &&
    cityIds.some(
      (id) =>
        !bootstrap.tenants.some(
          (item) => item.id === id && item.partnerIds.includes(draft.companyId),
        ),
    )
  )
    errors.push("Выберите только города выбранной компании.");
  if (!draft.steps.length) errors.push("Добавьте хотя бы одно сообщение.");
  // Переходы, развилки и доли когорт проверяет общая проверка сценария — теми
  // же словами, что и бэкенд.
  errors.push(...scenarioErrors(draft.steps).map((error) => `${error}.`));
  for (const [index, step] of draft.steps.entries()) {
    // Пауза, развилка, когорта и выход не имеют канала, текста и промокода —
    // всё, что ниже, относится только к сообщениям.
    if (step.type === "segmentSplit" && step.split) {
      rules = 0;
      walk(step.split.condition, 0, true);
      if (rules > 30)
        errors.push(`Условие ${index + 1}: допускается не больше 30 правил.`);
    }
    if (!isMessage(step)) continue;
    errors.push(
      ...unreadPushFallbackErrors(draft.steps, index).map(
        (error) => `Шаг ${index + 1}: ${error}`,
      ),
    );
    const promo = normalizeStepPromo(step, draft.promoCode);
    const hasVariable = stepHasPromoVariable(step);
    if (
      promo.promoMode === "shared" &&
      (!promo.promoCode?.trim() ||
        Array.from(promo.promoCode.trim()).length > 100)
    )
      errors.push(
        `Шаг ${index + 1}: укажите общий промокод — от 1 до 100 символов.`,
      );
    if (promo.promoMode === "pool" && !promo.promoPoolId)
      errors.push(`Шаг ${index + 1}: выберите серию уникальных промокодов.`);
    if (promo.promoMode === "pool" && !hasVariable)
      errors.push(
        `Шаг ${index + 1}: вставьте переменную «Промокод» в сообщение.`,
      );
    if (promo.promoMode === "none" && hasVariable)
      errors.push(
        `Шаг ${index + 1}: выберите промокод или уберите переменную из текста.`,
      );
    if (
      checkChannelAvailability &&
      !isAvailable(bootstrap.capabilities[step.channel])
    )
      errors.push(
        `Шаг ${index + 1}: канал «${CHANNEL_LABELS[step.channel]}» пока недоступен.`,
      );
    if (step.channel === "sms" && !cleanSmsTemplate(step.smsText))
      errors.push(`Шаг ${index + 1}: заполните текст SMS.`);
    if (step.channel === "email")
      errors.push(
        ...validateCampaignEmail(step.email).map(
          (error) => `Шаг ${index + 1}: ${error}`,
        ),
      );
    if (step.channel === "push")
      errors.push(
        ...validatePush(step.push).map((error) => `Шаг ${index + 1}: ${error}`),
      );
    const texts =
      step.channel === "sms"
        ? [step.smsText]
        : step.channel === "push"
          ? pushVariableTexts(step.push)
          : emailVariableTexts(step.email);
    for (const text of texts) {
      for (const variable of text.match(/\{[^{}]*\}/g) ?? []) {
        if (
          [
            "{favorite_dish}",
            "{любимое_блюдо}",
            "{name}",
            "{имя}",
            "{promo}",
            "{промокод}",
          ].includes(variable)
        )
          continue;
        if (variable === "{unsubscribe}" || variable === "{отписка}") {
          if (step.channel === "push")
            errors.push(
              `Шаг ${index + 1}: переменная отказа от рассылки не используется в пуше.`,
            );
          else if (step.channel === "email")
            errors.push(
              `Шаг ${index + 1}: уберите ${variable} из письма. Личная ссылка отказа добавляется автоматически внизу письма.`,
            );
        } else {
          errors.push(
            `Шаг ${index + 1}: неизвестная переменная ${variable}. Используйте кнопки «Имя» и «Промокод» в редакторе.`,
          );
        }
      }
    }
    if (
      !Number.isInteger(step.waitHours) ||
      step.waitHours <
        (step.fallback === "failed" || step.fallback === "always" ? 0 : 1) ||
      step.waitHours > 720
    )
      errors.push(
        `Шаг ${index + 1}: ожидание от ${step.fallback === "failed" ? 0 : 1} до 720 часов.`,
      );
    if (step.onUnavailable && step.onUnavailable !== "next")
      errors.push(
        `Шаг ${index + 1}: выберите правило для недоступного контакта.`,
      );
  }
  if (
    draft.sendTimeMode &&
    !["fixed", "last_order", "habit"].includes(draft.sendTimeMode)
  )
    errors.push("Выберите способ определения времени отправки.");
  // Сдвиг нужен и режиму «в привычный час»: по нему считается откат на время последнего заказа.
  if (
    (draft.sendTimeMode === "last_order" || draft.sendTimeMode === "habit") &&
    (!Number.isInteger(draft.sendOffsetMinutes) ||
      (draft.sendOffsetMinutes ?? -1) < 0 ||
      (draft.sendOffsetMinutes ?? 1441) > 1440)
  )
    errors.push("Сдвиг времени — целое число от 0 до 1440 минут.");
  if (
    !Number.isInteger(draft.sendHour) ||
    draft.sendHour < 10 ||
    draft.sendHour > 20
  )
    errors.push("Час отправки должен быть с 10 до 20 по местному времени.");
  if (
    !Number.isInteger(draft.frequencyDays) ||
    draft.frequencyDays < 1 ||
    draft.frequencyDays > 365
  )
    errors.push("Ограничение частоты — от 1 до 365 дней.");
  if (
    !Number.isInteger(draft.reentryDays) ||
    draft.reentryDays < 1 ||
    draft.reentryDays > 730
  )
    errors.push("Повторное участие — от 1 до 730 дней.");
  if (
    !Number.isInteger(draft.holdoutPercent) ||
    draft.holdoutPercent < 0 ||
    draft.holdoutPercent > 30
  )
    errors.push("Контрольная группа — от 0 до 30%.");
  if (
    !Number.isInteger(draft.attributionWindowDays) ||
    draft.attributionWindowDays < 1 ||
    draft.attributionWindowDays > 30
  )
    errors.push("Период учёта заказов — целое число от 1 до 30 дней.");
  if (!draft.stopOnOrder)
    errors.push("Остановка после нового заказа должна быть включена.");
  return [...new Set(errors)];
}

export function can(
  bootstrap: MarketingBootstrap,
  permission: string,
): boolean {
  return (
    bootstrap.scope.permissions?.includes("*") ||
    bootstrap.scope.permissions?.includes(`retention:${permission}`) ||
    false
  );
}

export function templateUnavailableReason(
  template: CampaignTemplate,
  bootstrap: MarketingBootstrap,
): string {
  if (!template.availability.available)
    return template.availability.reason || "Сценарий пока недоступен";
  const missing = template.availability.requires?.find(
    (key) => !isAvailable(bootstrap.capabilities[key]),
  );
  return missing ? capabilityReason(bootstrap.capabilities[missing]) : "";
}

/** Совместимость с прежними шаблонами: пользовательские URL остаются в тексте. */
export function cleanSmsTemplate(text: string): string {
  return text
    .replace(
      /(^|[ \t\r\n])(?:отказ|отписка)[ \t]*:[ \t]*\{(?:unsubscribe|отписка)\}/gi,
      "$1",
    )
    .replaceAll("{unsubscribe}", "")
    .replaceAll("{отписка}", "")
    .trim();
}

export function smsPreview(text: string, name = "Гость", promo = ""): string {
  return personalize(cleanSmsTemplate(text), name, promo);
}

/** Автоматическое правило ждёт новых совпадений; пауза сохраняет прежние участия. */
export function audienceBlocksLaunch(
  mode: MarketingDraft["mode"],
  status: string,
  audience: number,
): boolean {
  return mode === "once" && status === "draft" && audience === 0;
}

/** Старые сохранённые кампании оценивались за семь дней. */
export function savedAttributionWindow(value: number | undefined): number {
  return value == null || value === 0 ? 7 : value;
}

export function durationLabel(hours: number): string {
  if (hours === 0) return "без ожидания";
  if (!Number.isFinite(hours) || hours < 0) return "не задано";
  const days = hours % 24 === 0;
  const value = days ? hours / 24 : hours;
  const forms = days
    ? { one: "день", few: "дня", many: "дней", other: "дня" }
    : { one: "час", few: "часа", many: "часов", other: "часа" };
  const plural = new Intl.PluralRules("ru-RU").select(
    value,
  ) as keyof typeof forms;
  return `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(value)} ${forms[plural] ?? forms.other}`;
}

/**
 * Длина и число частей SMS считаются в smsParts.ts — зеркале Go
 * (sms_parts.go): GSM-7 160/153, иначе UCS-2 70/67. Здесь только реэкспорт,
 * чтобы прежние импорты из model не менялись.
 */
export { smsLength, smsParts } from "./smsParts";
